import { isSsoEnabled } from '../auth/cognitoConfig';
import { getAuthToken, triggerAuthExpired } from '../auth/authToken';

export interface RuntimeApiConfig {
  apiUrl: string;
  token: string;
}

const API_URL_KEY = 'easypost_api_url';
const API_TOKEN_KEY = 'easypost_api_token';

export const API_CONFIG_CHANGED_EVENT = 'easypost:api-config-changed';
export const API_AUTH_REQUIRED_EVENT = 'easypost:api-auth-required';

function readEnv(): Partial<ImportMetaEnv> {
  return (import.meta.env ?? {}) as Partial<ImportMetaEnv>;
}

export function getDefaultApiUrl(): string {
  const configuredUrl = readEnv().VITE_API_URL?.trim();
  if (configuredUrl) return normalizeApiUrl(configuredUrl);
  return '';
}

export function normalizeApiUrl(value: string): string {
  return value.trim().replace(/\/+$/, '');
}

interface ApiUrlPolicy {
  development: boolean;
  requireTrustedOrigin: boolean;
  trustedOrigins: Iterable<string>;
}

export function isAllowedApiUrlForPolicy(value: string, policy: ApiUrlPolicy): boolean {
  if (!value) return policy.development;
  try {
    const parsed = new URL(value);
    if (parsed.username || parsed.password || parsed.search || parsed.hash) return false;
    if (parsed.protocol !== 'https:' && !(policy.development && parsed.protocol === 'http:')) {
      return false;
    }
    if (!policy.requireTrustedOrigin) return true;
    return new Set(policy.trustedOrigins).has(parsed.origin);
  } catch {
    return false;
  }
}

function getTrustedSsoApiOrigins(): Set<string> {
  const env = readEnv();
  const configured = [
    getDefaultApiUrl(),
    ...(env.VITE_ALLOWED_API_ORIGINS || '').split(','),
  ];
  const origins = new Set<string>();
  for (const value of configured) {
    try {
      const normalized = normalizeApiUrl(value);
      if (normalized) origins.add(new URL(normalized).origin);
    } catch {
      // Invalid build-time entries are ignored; production SSO then fails closed.
    }
  }
  return origins;
}

export function isAllowedApiUrl(value: string): boolean {
  const development = Boolean(readEnv().DEV);
  return isAllowedApiUrlForPolicy(value, {
    development,
    // A legacy shared token is deliberately entered for the selected Worker.
    // A Cognito token, however, is valid at the production API and must never
    // be sent to an arbitrary HTTPS origin.
    requireTrustedOrigin: isSsoEnabled() && !development,
    trustedOrigins: getTrustedSsoApiOrigins(),
  });
}

export function getStoredApiUrl(): string {
  try {
    return normalizeApiUrl(sessionStorage.getItem(API_URL_KEY) ?? getDefaultApiUrl());
  } catch {
    return getDefaultApiUrl();
  }
}

export function getRuntimeApiConfig(): RuntimeApiConfig | null {
  try {
    const ssoEnabled = isSsoEnabled();
    let apiUrl = getStoredApiUrl();
    if (ssoEnabled && !isAllowedApiUrl(apiUrl)) {
      const defaultApiUrl = getDefaultApiUrl();
      // An obsolete override cannot block access to the deployed SSO API.
      // Resolve configuration through the trusted build endpoint without rewriting the
      // override or any prepared purchase's original connection identity.
      if (isAllowedApiUrl(defaultApiUrl)) apiUrl = defaultApiUrl;
    }
    if (!isAllowedApiUrl(apiUrl)) return null;

    // In SSO mode the bearer is the Cognito access token owned by the OIDC
    // session, not a value stored in this module.
    const token = ssoEnabled
      ? getAuthToken() ?? ''
      : sessionStorage.getItem(API_TOKEN_KEY)?.trim() ?? '';

    if (!token) return null;

    return { apiUrl, token };
  } catch {
    return null;
  }
}

/**
 * Persist only an API URL override; in SSO mode the bearer comes from Cognito
 * rather than a typed token.
 */
export function saveRuntimeApiUrl(url: string): string {
  const normalized = normalizeApiUrl(url);
  sessionStorage.setItem(API_URL_KEY, normalized);
  window.dispatchEvent(new Event(API_CONFIG_CHANGED_EVENT));
  return normalized;
}

export function saveRuntimeApiConfig(config: RuntimeApiConfig): RuntimeApiConfig {
  const normalized = {
    apiUrl: normalizeApiUrl(config.apiUrl),
    token: config.token.trim(),
  };

  sessionStorage.setItem(API_URL_KEY, normalized.apiUrl);
  sessionStorage.setItem(API_TOKEN_KEY, normalized.token);
  window.dispatchEvent(new Event(API_CONFIG_CHANGED_EVENT));
  return normalized;
}

export function clearRuntimeApiToken(failedRequestConfig?: RuntimeApiConfig): void {
  if (failedRequestConfig) {
    const current = getRuntimeApiConfig();
    // A request can finish after token renewal or a connection change. Its
    // rejection only invalidates the credentials that it actually sent.
    if (
      !current ||
      current.apiUrl !== failedRequestConfig.apiUrl ||
      current.token !== failedRequestConfig.token
    ) return;
  }
  if (isSsoEnabled()) {
    // Drop the OIDC session; the SSO gate will show the sign-in screen again.
    triggerAuthExpired();
    window.dispatchEvent(new Event(API_AUTH_REQUIRED_EVENT));
    return;
  }
  try {
    sessionStorage.removeItem(API_TOKEN_KEY);
  } catch {
    // The gate will still be notified even if storage is unavailable.
  }
  window.dispatchEvent(new Event(API_AUTH_REQUIRED_EVENT));
}

export function requireRuntimeApiConfig(): RuntimeApiConfig {
  const config = getRuntimeApiConfig();
  if (!config) {
    window.dispatchEvent(new Event(API_AUTH_REQUIRED_EVENT));
    throw new Error('API connection is not configured.');
  }
  return config;
}
